import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { api } from '../services/api';
import { UserSession } from '../types';
import { PrivacyPolicyModal } from '../components/PrivacyPolicyModal';
import { Shield, KeyRound, Mail, Smartphone, ArrowRight, UserPlus, Building, CheckCircle2, ArrowLeft } from 'lucide-react';

interface AuthPageProps {
  onLoginSuccess: (session: UserSession) => void;
}

export const AuthPage: React.FC<AuthPageProps> = ({ onLoginSuccess }) => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [authTab, setAuthTab] = useState<'signin' | 'signup'>(searchParams.get('tab') === 'signup' ? 'signup' : 'signin');
  const [mode, setMode] = useState<'request' | 'verify'>('request');
  const [contactType, setContactType] = useState<'EMAIL' | 'SMS'>('EMAIL');
  
  // Sign-in fields
  const [contact, setContact] = useState('');
  const [sessionId, setSessionId] = useState('');
  const [otpInput, setOtpInput] = useState('');

  // Sign-up fields
  const [signUpName, setSignUpName] = useState('');
  const [signUpEmail, setSignUpEmail] = useState('');
  const [signUpPhone, setSignUpPhone] = useState('');
  const [userMode, setUserMode] = useState<'B2C' | 'B2B'>('B2C');
  const [companyName, setCompanyName] = useState('');
  const [taxId, setTaxId] = useState('');
  const [acceptedPrivacy, setAcceptedPrivacy] = useState(false);
  const [showPrivacyModal, setShowPrivacyModal] = useState(false);

  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');

  const handleRequestOTP = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!contact.trim()) return;
    setLoading(true);
    setMessage('');

    try {
      const res = await api.requestOTP(contact, contactType);
      setSessionId(res.sessionId);
      setMessage(res.message);
      setMode('verify');
    } catch (err: any) {
      setMessage(err.message || 'Error requesting OTP code');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOTP = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!otpInput.trim()) return;
    setLoading(true);
    setMessage('');

    try {
      const res = await api.verifyOTP(contact, sessionId, otpInput);
      onLoginSuccess({
        token: res.token,
        userId: res.user.userId,
        email: res.user.email,
        role: res.user.role,
        companyId: res.user.companyId,
      });
    } catch (err: any) {
      setMessage(err.message || 'Invalid OTP code');
    } finally {
      setLoading(false);
    }
  };

  const handleSignUpSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!acceptedPrivacy) {
      alert(t('Debe aceptar la Política de Privacidad para continuar.', 'You must accept the Privacy Policy to proceed.'));
      return;
    }

    setLoading(true);
    setMessage('');

    try {
      // Trigger request OTP after registering email
      const res = await api.requestOTP(signUpEmail, 'EMAIL');
      setContact(signUpEmail);
      setSessionId(res.sessionId);
      setMessage(t('Cuenta registrada. Código de acceso OTP enviado a su correo.', 'Account registered. OTP code sent to your email.'));
      setAuthTab('signin');
      setMode('verify');
    } catch (err: any) {
      setMessage(err.message || 'Error creating account');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '40px 20px',
      background: 'radial-gradient(circle at top right, rgba(59, 130, 246, 0.15), transparent 40%)',
      flexDirection: 'column',
    }}>
      {/* Back to landing link */}
      <button
        onClick={() => navigate('/')}
        style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', marginBottom: '20px', fontWeight: 600 }}
      >
        <ArrowLeft size={15} />
        {t('Volver al inicio', 'Back to home')}
      </button>
      <div className="glass-card" style={{ width: '100%', maxWidth: '480px', padding: '36px' }}>
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: '20px' }}>
          <div style={{
            width: '56px',
            height: '56px',
            borderRadius: '16px',
            background: 'var(--gradient-brand)',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: 'var(--shadow-glow)',
            marginBottom: '12px'
          }}>
            <Shield size={28} color="#ffffff" />
          </div>
          <h2 style={{ fontSize: '24px', fontWeight: 800 }}>
            e-learning <span style={{ color: 'var(--accent-primary)', fontSize: '14px', fontWeight: 600 }}>by akretrix</span>
          </h2>
          <p style={{ fontSize: '13px', color: 'var(--text-secondary)', marginTop: '4px' }}>
            {t('Capacitación Corporativa e Insignias Verificables', 'Corporate Compliance & Verifiable Credentials')}
          </p>
        </div>

        {/* Auth Tab Switcher */}
        <div style={{ display: 'flex', background: 'var(--bg-secondary)', padding: '4px', borderRadius: 'var(--radius-sm)', border: '1px solid var(--border-color)', marginBottom: '24px' }}>
          <button
            type="button"
            style={{
              flex: 1,
              padding: '8px',
              border: 'none',
              borderRadius: '4px',
              fontWeight: 700,
              fontSize: '13px',
              cursor: 'pointer',
              background: authTab === 'signin' ? 'var(--accent-primary)' : 'transparent',
              color: authTab === 'signin' ? '#ffffff' : 'var(--text-secondary)'
            }}
            onClick={() => { setAuthTab('signin'); setMode('request'); }}
          >
            {t('Iniciar Sesión', 'Sign In')}
          </button>

          <button
            type="button"
            style={{
              flex: 1,
              padding: '8px',
              border: 'none',
              borderRadius: '4px',
              fontWeight: 700,
              fontSize: '13px',
              cursor: 'pointer',
              background: authTab === 'signup' ? 'var(--accent-primary)' : 'transparent',
              color: authTab === 'signup' ? '#ffffff' : 'var(--text-secondary)'
            }}
            onClick={() => setAuthTab('signup')}
          >
            <UserPlus size={14} style={{ verticalAlign: 'middle', marginRight: '4px' }} />
            {t('Crear Cuenta', 'Sign Up')}
          </button>
        </div>

        {/* Message Banner */}
        {message && (
          <div style={{
            padding: '10px 14px',
            borderRadius: 'var(--radius-sm)',
            background: message.toLowerCase().includes('error') || message.toLowerCase().includes('invalid')
              ? 'rgba(239, 68, 68, 0.15)'
              : 'rgba(16, 185, 129, 0.15)',
            border: message.toLowerCase().includes('error') || message.toLowerCase().includes('invalid')
              ? '1px solid var(--accent-danger)'
              : '1px solid var(--accent-success)',
            color: message.toLowerCase().includes('error') || message.toLowerCase().includes('invalid')
              ? 'var(--accent-danger)'
              : 'var(--accent-success)',
            fontSize: '12px',
            marginBottom: '20px',
            textAlign: 'center'
          }}>
            {message}
          </div>
        )}

        {/* SIGN IN TAB */}
        {authTab === 'signin' && (
          mode === 'request' ? (
            <form onSubmit={handleRequestOTP}>
              <div style={{ display: 'flex', gap: '8px', marginBottom: '16px' }}>
                <button
                  type="button"
                  className={`btn ${contactType === 'EMAIL' ? 'btn-primary' : 'btn-secondary'}`}
                  style={{ flex: 1, padding: '8px' }}
                  onClick={() => setContactType('EMAIL')}
                >
                  <Mail size={14} /> Email (SES)
                </button>
                <button
                  type="button"
                  className={`btn ${contactType === 'SMS' ? 'btn-primary' : 'btn-secondary'}`}
                  style={{ flex: 1, padding: '8px' }}
                  onClick={() => setContactType('SMS')}
                >
                  <Smartphone size={14} /> SMS (SNS)
                </button>
              </div>

              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: 700, color: 'var(--text-secondary)', marginBottom: '6px' }}>
                  {contactType === 'EMAIL' ? t('Correo Electrónico Corporativo', 'Corporate Email Address') : t('Número Telefónico (E.164)', 'Phone Number (E.164)')}
                </label>
                <input
                  type={contactType === 'EMAIL' ? 'email' : 'tel'}
                  className="input-field"
                  placeholder={contactType === 'EMAIL' ? 'usuario@empresa.com' : '+57 300 123 4567'}
                  value={contact}
                  onChange={(e) => setContact(e.target.value)}
                  required
                />
              </div>

              <button type="submit" className="btn btn-primary" style={{ width: '100%', padding: '12px' }} disabled={loading}>
                {loading ? t('Enviando Código...', 'Sending Code...') : t('Solicitar Código OTP', 'Request OTP Code')}
                <ArrowRight size={16} />
              </button>
            </form>
          ) : (
            <form onSubmit={handleVerifyOTP}>
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: 700, color: 'var(--text-secondary)', marginBottom: '6px' }}>
                  {t('Ingrese el Código OTP de 6 Dígitos', 'Enter 6-Digit Passcode')}
                </label>
                <input
                  type="text"
                  className="input-field"
                  placeholder="123456"
                  maxLength={6}
                  style={{ letterSpacing: '6px', fontSize: '20px', textAlign: 'center', fontWeight: 700 }}
                  value={otpInput}
                  onChange={(e) => setOtpInput(e.target.value)}
                  required
                />
              </div>

              <button type="submit" className="btn btn-primary" style={{ width: '100%', padding: '12px' }} disabled={loading}>
                <KeyRound size={16} />
                {loading ? t('Verificando...', 'Verifying...') : t('Validar e Iniciar Sesión', 'Validate & Sign In')}
              </button>
            </form>
          )
        )}

        {/* SIGN UP TAB */}
        {authTab === 'signup' && (
          <form onSubmit={handleSignUpSubmit}>
            <div style={{ marginBottom: '14px' }}>
              <label style={{ display: 'block', fontSize: '12px', fontWeight: 700, color: 'var(--text-secondary)', marginBottom: '4px' }}>
                {t('Nombre Completo', 'Full Name')}
              </label>
              <input
                type="text"
                className="input-field"
                placeholder="Juan Perez"
                value={signUpName}
                onChange={(e) => setSignUpName(e.target.value)}
                required
              />
            </div>

            <div style={{ marginBottom: '14px' }}>
              <label style={{ display: 'block', fontSize: '12px', fontWeight: 700, color: 'var(--text-secondary)', marginBottom: '4px' }}>
                {t('Correo Electrónico', 'Email Address')}
              </label>
              <input
                type="email"
                className="input-field"
                placeholder="juan@empresa.com"
                value={signUpEmail}
                onChange={(e) => setSignUpEmail(e.target.value)}
                required
              />
            </div>

            <div style={{ marginBottom: '14px' }}>
              <label style={{ display: 'block', fontSize: '12px', fontWeight: 700, color: 'var(--text-secondary)', marginBottom: '4px' }}>
                {t('Teléfono (Opcional)', 'Phone (Optional)')}
              </label>
              <input
                type="tel"
                className="input-field"
                placeholder="+57 300 123 4567"
                value={signUpPhone}
                onChange={(e) => setSignUpPhone(e.target.value)}
              />
            </div>

            {/* User Mode Selector */}
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', fontSize: '12px', fontWeight: 700, color: 'var(--text-secondary)', marginBottom: '6px' }}>
                {t('Tipo de Cuenta', 'Account Type')}
              </label>
              <div style={{ display: 'flex', gap: '8px' }}>
                <button
                  type="button"
                  className={`btn ${userMode === 'B2C' ? 'btn-primary' : 'btn-secondary'}`}
                  style={{ flex: 1, padding: '8px', fontSize: '12px' }}
                  onClick={() => setUserMode('B2C')}
                >
                  {t('Individual (B2C)', 'Individual Learner')}
                </button>
                <button
                  type="button"
                  className={`btn ${userMode === 'B2B' ? 'btn-primary' : 'btn-secondary'}`}
                  style={{ flex: 1, padding: '8px', fontSize: '12px' }}
                  onClick={() => setUserMode('B2B')}
                >
                  <Building size={14} /> {t('Empresa (B2B Admin)', 'Company Admin')}
                </button>
              </div>
            </div>

            {userMode === 'B2B' && (
              <div style={{ padding: '12px', background: 'var(--bg-secondary)', borderRadius: 'var(--radius-sm)', border: '1px solid var(--border-color)', marginBottom: '16px' }}>
                <div style={{ marginBottom: '10px' }}>
                  <label style={{ display: 'block', fontSize: '11px', fontWeight: 700, color: 'var(--text-secondary)', marginBottom: '4px' }}>
                    {t('Nombre de la Empresa', 'Company Name')}
                  </label>
                  <input
                    type="text"
                    className="input-field"
                    placeholder="Acme Corp S.A.S"
                    value={companyName}
                    onChange={(e) => setCompanyName(e.target.value)}
                    required={userMode === 'B2B'}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '11px', fontWeight: 700, color: 'var(--text-secondary)', marginBottom: '4px' }}>
                    {t('NIT / Tax ID', 'Tax Identification Number')}
                  </label>
                  <input
                    type="text"
                    className="input-field"
                    placeholder="900.123.456-7"
                    value={taxId}
                    onChange={(e) => setTaxId(e.target.value)}
                  />
                </div>
              </div>
            )}

            {/* Privacy Policy Agreement Checkbox */}
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', marginBottom: '20px' }}>
              <input
                type="checkbox"
                id="privacyCheck"
                checked={acceptedPrivacy}
                onChange={(e) => setAcceptedPrivacy(e.target.checked)}
                style={{ marginTop: '3px', cursor: 'pointer' }}
                required
              />
              <label htmlFor="privacyCheck" style={{ fontSize: '12px', color: 'var(--text-secondary)', cursor: 'pointer', lineHeight: 1.4 }}>
                {t('Acepto la ', 'I agree to the ')}
                <span
                  style={{ color: 'var(--accent-primary)', textDecoration: 'underline', fontWeight: 600 }}
                  onClick={(e) => { e.preventDefault(); setShowPrivacyModal(true); }}
                >
                  {t('Política de Privacidad y Protección de Datos (RGPD / Ley 1581)', 'Privacy Policy & Data Protection Terms')}
                </span>.
              </label>
            </div>

            <button type="submit" className="btn btn-primary" style={{ width: '100%', padding: '12px' }} disabled={loading || !acceptedPrivacy}>
              <UserPlus size={16} />
              {loading ? t('Registrando Cuenta...', 'Creating Account...') : t('Registrarse y Recibir OTP', 'Register & Receive OTP')}
            </button>
          </form>
        )}
      </div>

      {showPrivacyModal && (
        <PrivacyPolicyModal onClose={() => setShowPrivacyModal(false)} />
      )}
    </div>
  );
};
